import { useState, useEffect, useMemo, useRef } from 'react';
import { createChart, CandlestickSeries, HistogramSeries, LineSeries } from 'lightweight-charts';
import {getList} from '../../../../common/BackendProvider';
import {toast} from "react-toastify";

const FALLBACK_CHART_HEIGHT = 400;
const intervals = ["1w", "1m", "3m", "1y", "5y"];

const intervalLabels = {
    '1w': 'daily',
    '1m': 'daily',
    '3m': 'weekly',
    '1y': 'monthly',
    '5y': 'monthly',
};

const toChartTimestamp = (timestamp) => {
    if (!timestamp) {
        return null;
    }

    const normalized = typeof timestamp === 'string' ? timestamp.replace(' ', 'T') : timestamp;
    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return Math.floor(date.getTime() / 1000);
}

const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : null;
}

const formatCurrency = (value) => {
    if (!Number.isFinite(value)) {
        return '-';
    }

    return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

const formatPercent = (value) => {
    if (!Number.isFinite(value)) {
        return '-';
    }

    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

const formatVolume = (value) => {
    if (!Number.isFinite(value)) {
        return '-';
    }

    if (value >= 1000000000) {
        return `${(value / 1000000000).toFixed(2)}B`;
    }

    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(2)}M`;
    }

    if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
    }

    return `${Math.round(value)}`;
}

const formatTick = (time, interval) => {
    const date = new Date(time * 1000);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    if (interval === '1w' || interval === '1m') {
        return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }

    if (interval === '3m') {
        return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }

    if (interval === '1y') {
        return date.toLocaleDateString(undefined, { month: 'short' });
    }

    return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

const formatCrosshairTime = (time, interval) => {
    const date = new Date(time * 1000);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    if (interval === '5y') {
        return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }

    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

const normalizeStockRows = (rows) => {
    return (rows || [])
        .map((row) => {
            const time = toChartTimestamp(row.timestamp);
            const open = toNumber(row.open);
            const high = toNumber(row.high);
            const low = toNumber(row.low);
            const close = toNumber(row.close);
            const volume = toNumber(row.volume) || 0;

            if (time === null || close === null) {
                return null;
            }

            return {
                time,
                open,
                high,
                low,
                close,
                volume,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.time - b.time)
        .filter((row, index, rows) => index === 0 || row.time !== rows[index - 1].time);
}

const hasOhlcData = (rows) => {
    return rows.length > 0 && rows.every((row) => (
        row.open !== null &&
        row.high !== null &&
        row.low !== null &&
        row.close !== null
    ));
}

const useFinancialChart = (containerRef, rows, interval) => {
    useEffect(() => {
        const container = containerRef.current;
        if (!container || rows.length === 0) {
            return undefined;
        }

        const chartHeight = () => container.clientHeight || FALLBACK_CHART_HEIGHT;

        const chart = createChart(container, {
            width: container.clientWidth,
            height: chartHeight(),
            layout: {
                background: { color: '#ffffff' },
                textColor: '#4b5563',
                fontFamily: 'inherit',
            },
            grid: {
                vertLines: { color: '#e5e7eb', style: 2 },
                horzLines: { color: '#e5e7eb', style: 2 },
            },
            rightPriceScale: {
                borderVisible: false,
                scaleMargins: {
                    top: 0.08,
                    bottom: 0.28,
                },
            },
            timeScale: {
                borderVisible: false,
                timeVisible: false,
                secondsVisible: false,
                tickMarkFormatter: (time) => formatTick(time, interval),
            },
            localization: {
                timeFormatter: (time) => formatCrosshairTime(time, interval),
            },
            crosshair: {
                mode: 1,
            },
        });

        if (hasOhlcData(rows)) {
            const candleSeries = chart.addSeries(CandlestickSeries, {
                upColor: '#16a34a',
                downColor: '#dc2626',
                borderUpColor: '#16a34a',
                borderDownColor: '#dc2626',
                wickUpColor: '#16a34a',
                wickDownColor: '#dc2626',
            });

            candleSeries.setData(rows.map((row) => ({
                time: row.time,
                open: row.open,
                high: row.high,
                low: row.low,
                close: row.close,
            })));
        } else {
            const lineSeries = chart.addSeries(LineSeries, {
                color: '#2563eb',
                lineWidth: 2,
            });

            lineSeries.setData(rows.map((row) => ({
                time: row.time,
                value: row.close,
            })));
        }

        const volumeSeries = chart.addSeries(HistogramSeries, {
            priceFormat: {
                type: 'volume',
            },
            priceScaleId: 'volume',
            color: '#cbd5e1',
        });

        chart.priceScale('volume').applyOptions({
            scaleMargins: {
                top: 0.78,
                bottom: 0,
            },
        });

        volumeSeries.setData(rows.map((row) => ({
            time: row.time,
            value: row.volume,
            color: row.open === null || row.close >= row.open ? 'rgba(22, 163, 74, 0.28)' : 'rgba(220, 38, 38, 0.28)',
        })));

        chart.timeScale().fitContent();

        const resizeObserver = new ResizeObserver(() => {
            chart.applyOptions({
                width: container.clientWidth,
                height: chartHeight(),
            });
            chart.timeScale().fitContent();
        });
        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
            chart.remove();
        };
    }, [containerRef, rows, interval]);
}

export const StockChart = ({company}) => {
    const chartContainerRef = useRef(null);
    const[stockData, setStockData] = useState([])
    const[interval, setInterval] = useState("1m")
    const[loading, setLoading] = useState(true)

    useEffect(() => {
        setLoading(true)
        getList("companies/" + company.symbol + "/stock" , {filter:{'interval': interval}}).then((response) => {
            setStockData(normalizeStockRows(response.data.stock_data))
            setLoading(false)
        }).catch(
            (e) => {
                toast.error("Error while loading stock data", {})
                setLoading(false)
            }
        )
    },[interval, company])

    useFinancialChart(chartContainerRef, stockData, interval);

    const latestQuote = useMemo(() => {
        if (stockData.length === 0) {
            return null;
        }

        const first = stockData[0];
        const latest = stockData[stockData.length - 1];
        const change = latest.close - first.close;
        const changePercent = first.close !== 0 ? (change / first.close) * 100 : 0;

        return {
            price: latest.close,
            change,
            changePercent,
        };
    }, [stockData]);

    const latestVolume = useMemo(() => {
        if (stockData.length === 0) {
            return null;
        }

        return stockData[stockData.length - 1].volume;
    }, [stockData]);

    return (
        <>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                    {intervals.map((i) => {
                        return (
                            <button
                                key={i}
                                className={`min-w-14 rounded-md border px-4 py-2 text-sm font-semibold transition ${interval === i ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-emerald-400 hover:text-emerald-700"}`}
                                onClick={()=>setInterval(i)}
                                type="button"
                            >
                                {i}
                            </button>
                        )
                    })}
                </div>
                {latestQuote !== null && (
                    <div className="text-right">
                        <div className="text-3xl font-semibold text-slate-950">{formatCurrency(latestQuote.price)}</div>
                        <div className={`text-sm font-medium ${latestQuote.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {latestQuote.change >= 0 ? '+' : ''}{formatCurrency(latestQuote.change)} ({formatPercent(latestQuote.changePercent)})
                        </div>
                        <div className="mt-1 text-xs font-medium uppercase tracking-[0.08em] text-slate-400">
                            {intervalLabels[interval]} candles {latestVolume !== null ? `- Vol ${formatVolume(latestVolume)}` : ''}
                        </div>
                    </div>
                )}
            </div>
            {loading ? (
                <div className="flex h-[340px] w-full items-center justify-center rounded-md border border-slate-200 text-emerald-600 sm:h-[400px]">
                    <span>Loading...</span>
                </div>
            ) : (
                stockData.length === 0 ? (
                    <div className="flex h-[340px] w-full items-center justify-center rounded-md border border-slate-200 text-gray-500 sm:h-[400px]">
                        <span>No stock price data available for this interval.</span>
                    </div>
                ) : (
                    <div ref={chartContainerRef} className="h-[340px] w-full overflow-hidden sm:h-[400px]" />
                )
            )}
        </>
    )
}
